import React, {
    useState,
    useRef,
    useCallback,
    useMemo,
    createContext,
    useContext,
    useEffect,
} from "react";
import PropTypes from "prop-types";
import Table from "../../components/table/Table";
import ActionPopup from "../../components/ActionPopup";
import ActionIcon from "../../assets/icons/action-icon.svg";
import ExcelIcon from "../../assets/icons/excel-document-icon.svg";
import useStickyHeight from "../../hooks/useStickyHeight";
import HelpSection from "../help/HelpSection";
import DefinitionContent from "../help/DefinitionContent";
import { useSelector, useDispatch } from "react-redux";
import GTM from "../../GTM";
import ReportButtons from "../../components/ui/ReportButtons";
import Loader from "../../components/ui/Loader";
import NoRecords from "../../components/alerts/NoRecords";
import RequestFailed from "../../components/alerts/RequestFailed";
import {
    DASHBOARD_MESSAGES,
    NO_TAX_REPORT_RECORDS_MESSAGE,
} from "../../constants";
import documentsApiSlice from "../../slices/documentsApiSlice";
import {
    addExportJob,
    updateExportJob,
    setShowExportToast,
} from "../../slices/highVolumeSlice";
import {
    getSelectedIsfOrganizationIds,
    triggerDownload,
} from "../../utils/helpers";

const FIXED_WIDTH_COLUMN_HEADERS = new Set([
    "Policy Number",
    "Division Number",
    "Document Name",
    "Tax Begin Date",
    "Tax End Date",
    "Tax Period Year",
    "Document Date",
]);

const getDocumentName = (item) => {
    const rawName =
        item?.name ||
        item?.documentName ||
        item?.DocumentName ||
        item?.fileName ||
        item?.filename ||
        "document";

    return (
        String(rawName)
            .replace(/[<>:"/\\|?*]+/g, "_")
            .trim() || "document"
    );
};

const getDocumentActionItems = (documentName, onDocumentClick) => [
    {
        key: "pdf",
        className: "px-2 py-1",
        icon: (
            <img
                src={ExcelIcon}
                alt=""
                aria-hidden="true"
                width="18"
                height="18"
                className="me-2"
            />
        ),
        label: documentName,
        onClick: onDocumentClick,
    },
];

const ActionCellContext = createContext({
    selectedActionIndex: null,
    setSelectedActionIndex: () => {},
    onDownloadDocument: () => {},
});

function DocumentActionCell({ row, containerRef }) {
    const { selectedActionIndex, setSelectedActionIndex, onDownloadDocument } =
        useContext(ActionCellContext);
    const index = row.index;
    const iconRef = useRef(null);
    const documentName = getDocumentName(row.original);
    const documentId = row.original?.documentId;
    const closePopup = () => setSelectedActionIndex(null);

    const handleDocumentClick = () => {
        closePopup();
        onDownloadDocument(documentId, documentName);
    };

    return (
        <div className="document-library-action-cell">
            <span
                ref={iconRef}
                className="d-flex align-items-center cursor-pointer"
                onClick={() =>
                    setSelectedActionIndex((prev) =>
                        prev === index ? null : index
                    )
                }
            >
                <img src={ActionIcon} alt="action" />
            </span>
            {selectedActionIndex === index && (
                <ActionPopup
                    toggleRef={iconRef}
                    containerRef={containerRef}
                    style={{ minWidth: 300 }}
                    onClose={closePopup}
                    items={getDocumentActionItems(
                        documentName,
                        handleDocumentClick
                    )}
                />
            )}
        </div>
    );
}

DocumentActionCell.propTypes = {
    row: PropTypes.shape({
        index: PropTypes.number.isRequired,
        original: PropTypes.object,
    }).isRequired,
    containerRef: PropTypes.shape({ current: PropTypes.any }),
};

DocumentActionCell.defaultProps = {
    containerRef: undefined,
};

export default function DocumentLibrary({
    dropdownSelectedReport,
    reportDescription,
    reportTableData,
    columns,
    dataViewType,
    isFetchingReportData,
    isErrorReport,
}) {
    const gtm = useMemo(() => new GTM(null), []);
    const dispatch = useDispatch();
    const [selectedActionIndex, setSelectedActionIndex] = useState(null);
    const documentLibrarySectionRef = useRef(null);
    const stickyFilterBarRef = useRef(null);
    const [openHelp, setOpenHelp] = useState(false);
    const definitionsExplained = useSelector(
        (state) => state?.helpContent?.definitionsExplained
    );
    const [showDefinitionContent, setShowDefinitionContent] = useState(false);
    const [selectedDefinitionContent, setSelectedDefinitionContent] =
        useState(null);
    useStickyHeight(
        stickyFilterBarRef,
        "--sticky-document-library-filter-height",
        documentLibrarySectionRef
    );

    const handleClose = useCallback(() => {
        setShowDefinitionContent(false);
        setOpenHelp(false);
    }, []);
    const closeHelp = useCallback(() => {
        setOpenHelp(false);
    }, []);

    const handleBack = () => {
        setShowDefinitionContent(false);
        setOpenHelp(true);
    };

    const handleDefinitionCard = useCallback(
        (helpCardName) => {
            const selectedContent = definitionsExplained?.find(
                (section) => section.Name === helpCardName
            );
            if (selectedContent) {
                setSelectedDefinitionContent(selectedContent);
                setShowDefinitionContent(true);
            }
            //GTM Event
            gtm.Event(
                "custom_event",
                "button",
                "click",
                "column dictionary",
                "report detail",
                dataViewType,
                "column_dictionary_click",
                "undefined"
            );
        },
        [definitionsExplained, dataViewType, gtm]
    );

    const backDrop = true;
    const helpSectionProps = {
        showDefinitionContent,
        selectedDefinitionContent,
        handleBack,
        backDrop,
        setShowDefinitionContent,
        handleHelpClose: handleClose,
    };

    const { user } = useSelector((state) => state.auth);
    const {
        selectedOrganizationsReports,
        selectedOrganizationsIdReports: reportId,
        selectedRelatedOrganizationsForReports,
    } = useSelector((state) => state.reportOrganization);
    const { selectedIsfOrganizationIds } = getSelectedIsfOrganizationIds(
        selectedOrganizationsReports,
        reportId,
        selectedRelatedOrganizationsForReports
    );

    // Fetch here (not in the row cell) so the request isn't aborted when the
    // action popup closes; progress is surfaced through the shared toast.
    const handleDownloadDocument = useCallback(
        async (documentId, fileName) => {
            if (!documentId) return;

            const jobId = `document-${documentId}-${Date.now()}`;
            dispatch(
                addExportJob({
                    jobId,
                    fileName,
                    status: "progress",
                    type: "download",
                })
            );
            dispatch(setShowExportToast(true));

            // Dispatch initiate directly so each concurrent download keeps its
            // own subscription and one finishing can't abort another.
            const queryResult = dispatch(
                documentsApiSlice.endpoints.getDocumentById.initiate({
                    documentId,
                    isfOrganizationId: selectedIsfOrganizationIds?.[0],
                    isfUserId: user?.userId,
                })
            );

            try {
                const result = await queryResult;

                const response = result?.data;
                if (result?.error || !response?.downloadUrl) {
                    dispatch(updateExportJob({ jobId, status: "failed" }));
                    dispatch(setShowExportToast(true));
                    return;
                }

                triggerDownload(response.downloadUrl, response.fileName);
                dispatch(
                    updateExportJob({
                        jobId,
                        status: "success",
                        downloadUrl: response.downloadUrl,
                    })
                );
                dispatch(setShowExportToast(true));
            } catch (error) {
                dispatch(updateExportJob({ jobId, status: "failed" }));
                dispatch(setShowExportToast(true));
            } finally {
                queryResult.unsubscribe();
            }
        },
        [dispatch, selectedIsfOrganizationIds, user]
    );

    const actionCellContextValue = useMemo(
        () => ({
            selectedActionIndex,
            setSelectedActionIndex,
            onDownloadDocument: handleDownloadDocument,
        }),
        [selectedActionIndex, handleDownloadDocument]
    );

    const tableColumns = useMemo(() => {
        const isFixedWidth = (obj) =>
            FIXED_WIDTH_COLUMN_HEADERS.has(obj.header) &&
            obj.size !== undefined;

        const mappedColumns = columns
            .filter(
                (obj) => obj.header !== "Action" && obj.header !== "Actions"
            )
            .map((obj) => ({
                Header: obj.header,
                accessor: obj.accessorKey,
                ...(isFixedWidth(obj)
                    ? { width: obj.size, className: "fixed-width-column" }
                    : {}),
            }));

        const actionColumn = {
            Header: "Actions",
            id: "action",
            accessor: "action",
            disableSortBy: true,
            disableFilters: true,
            Cell: ({ row }) => (
                <DocumentActionCell
                    row={row}
                    containerRef={documentLibrarySectionRef}
                />
            ),
        };

        return [
            {
                Header: "",
                id: "manage-my-reports-group",
                columns: [...mappedColumns, actionColumn],
            },
        ];
    }, [columns]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="section-card px-0p" ref={documentLibrarySectionRef}>
            <div
                className="sticky-filter-bar d-flex justify-content-between px-24p"
                style={{ borderRadius: 8 }}
                ref={stickyFilterBarRef}
            >
                <div className="section-heading">
                    {dropdownSelectedReport?.label || "Select"}
                </div>

                {openHelp && (
                    <HelpSection showModal={openHelp} closeHelp={closeHelp} />
                )}
                {showDefinitionContent && selectedDefinitionContent && (
                    <DefinitionContent helpSectionProps={helpSectionProps} />
                )}
                <div className="data-view-button w-auto">
                    {definitionsExplained
                        ?.filter(
                            (section) => section.ContentType === "columnDetails"
                        ) // Filter to only include the section with Order 3
                        .map((section) => (
                            <ReportButtons
                                key={section.Name} // Ensure each item has a unique key
                                label={section.Name}
                                onClick={() =>
                                    handleDefinitionCard(section.Name)
                                }
                                iconClass="fa-solid fa-circle-question"
                                ariaLabel={section.Name}
                                isDisabled={false}
                            />
                        ))}
                </div>
            </div>

            <div
                className="px-24p"
                style={{ fontSize: "12.8px", marginBottom: 18 }}
            >
                {reportDescription}
            </div>
            {isFetchingReportData && <Loader text="Searching" />}
            {!isFetchingReportData && isErrorReport && (
                <div
                    style={{
                        paddingLeft: 24,
                        paddingRight: 24,
                        paddingBottom: 24,
                    }}
                >
                    <RequestFailed
                        tabIndex={0}
                        message={DASHBOARD_MESSAGES.REQUEST_FAILED}
                        label="reports"
                        attribute1="Reports"
                        attribute3="reports_error"
                    />
                </div>
            )}
            {!isFetchingReportData && !isErrorReport && (
                <div>
                    <ActionCellContext.Provider value={actionCellContextValue}>
                        <Table
                            columns={tableColumns}
                            data={reportTableData || []}
                            emptyState={
                                <div className="px-24p py-24p">
                                    <NoRecords
                                        page="manage my report"
                                        label="documents"
                                        message={NO_TAX_REPORT_RECORDS_MESSAGE}
                                    />
                                </div>
                            }
                            baseClassName="document-library-table"
                            sticky={true}
                            pageStickyHeader={true}
                            defaultPageSize={20}
                            pagination={reportTableData?.length > 20}
                            striped={true}
                            bordered={true}
                            className="frequently-used-data"
                            pageName="manage my report"
                            paginationClassName="pt-20p"
                            responsiveConfig={{
                                type: "ROW",
                                striped: false,
                                screen: "md",
                                bordered: true,
                            }}
                            onSort={false}
                            enableColumnFilter={true}
                            options={[20, 50, 100]}
                        />
                    </ActionCellContext.Provider>
                </div>
            )}
        </div>
    );
}

DocumentLibrary.propTypes = {
    dropdownSelectedReport: PropTypes.shape({
        label: PropTypes.string,
    }),
    reportDescription: PropTypes.node,
    reportTableData: PropTypes.array,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            header: PropTypes.string,
            accessorKey: PropTypes.string,
            size: PropTypes.number,
        })
    ).isRequired,
    dataViewType: PropTypes.string,
    isErrorReport: PropTypes.bool,
};

DocumentLibrary.defaultProps = {
    dropdownSelectedReport: undefined,
    reportDescription: undefined,
    reportTableData: [],
    dataViewType: undefined,
    isErrorReport: false,
};
